import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EFlags, type TFlagsRecord } from '@feathr/blackbox';
import { Button, CardV2 as Card, Icon, SaveButton, Toggle } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useActionBar } from '@feathr/extender/state';
import { flagLabelMap } from '@feathr/extender/styles/flags';
import { cssVar, enumValues } from '@feathr/hooks';
import { useAccount } from '@feathr/report_components';

import * as styles from './FlagsPage.css';

function FlagsPage(): JSX.Element {
  const { t } = useTranslation();
  const account = useAccount();
  const { setRightActions } = useActionBar();
  const flags = account.get('flags', {} as TFlagsRecord);
  const initialFlags = useRef(flags);
  const allFlags = enumValues(EFlags);
  const liteFlags = [EFlags.showOnboarding, EFlags.enforceOnboarding];
  const remainingFlags = allFlags.filter((flag) => !liteFlags.includes(flag));
  const [changedFlags, setChangedFlags] = useState<string[]>([]);

  useEffect(() => {
    setRightActions(
      <SaveButton
        disabled={!account.isDirty}
        method={'patch'}
        model={account}
        onSave={(): void => {
          const changedFlagKeys = Object.keys(flags).filter(
            (flag) => !(flag in initialFlags.current) || flags[flag] !== initialFlags.current[flag],
          );
          setChangedFlags(changedFlagKeys);
        }}
      />,
    );
  }, [account.isDirty]);

  function handleToggle(flag: EFlags, value: boolean): void {
    const currentFlags = account.get('flags', {} as TFlagsRecord);
    account.set({ flags: { ...currentFlags, [flag]: value } });
  }

  function renderToggle(flag: EFlags): JSX.Element {
    return (
      <Toggle
        key={flag}
        label={
          <>
            {flagLabelMap.get(flag)}
            {changedFlags.includes(flag) && (
              <Button
                className={styles.warning}
                tooltip={t('Changes will not be visible until you refresh the page')}
                type={'icon-small'}
              >
                <Icon color={cssVar('--color-warning')} icon={faExclamationTriangle} />
              </Button>
            )}
          </>
        }
        layout={'well'}
        onChange={(): void => handleToggle(flag, !flags[flag])}
        value={flags[flag]}
      />
    );
  }

  return (
    <Page title={t('Flags')}>
      <Card>
        <Card.Header title={t('Flags')} />
        {account.isPlatformLite && (
          <Card.Content
            addVerticalGap={true}
            description={t('Only available on Lite accounts')}
            title={t('Feathr Platform - Lite')}
          >
            {liteFlags.map((flag) => {
              return renderToggle(flag);
            })}
          </Card.Content>
        )}
        <Card.Content addVerticalGap={true}>
          {remainingFlags.map((flag) => {
            return renderToggle(flag as EFlags);
          })}
        </Card.Content>
      </Card>
    </Page>
  );
}

export default observer(FlagsPage);
